import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";
import NewsletterSignup from "@/components/site/NewsletterSignup";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="grain relative bg-[#0A1128] text-slate-200 mt-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src="/logo.png"
              alt="Midnight Sun Working Dog Coalition"
              className="h-20 md:h-24 w-auto object-contain mb-6 -ml-2"
            />
            <div className="overline !text-amber-500 mb-4">Mission Brief</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-tight max-w-md">
              We don't chase crisis, We stand watch over it.
            </h3>
            <p className="mt-6 text-slate-400 max-w-md leading-relaxed">
              Animal-Assisted Environmental Stabilization Support for schools, veteran
              communities, rural facilities, behavioral health, and youth residential programs.
            </p>

            <NewsletterSignup />
          </div>

          <div className="md:col-span-3">
            <div className="overline !text-slate-500 mb-4">Navigate</div>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "Doctrine" },
                { to: "/partners", label: "Canine Partners" },
                { to: "/donate", label: "Support our work" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="link-underline text-slate-300 hover:text-amber-500 transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline !text-slate-500 mb-4">Headquarters</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-amber-500 shrink-0" />
                <span className="text-slate-300" data-testid="footer-address">{SITE.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-1 text-amber-500 shrink-0" />
                <a
                  data-testid="footer-email"
                  href={`mailto:${SITE.email}`}
                  className="link-underline text-slate-300 hover:text-amber-500 break-all"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-1 text-amber-500 shrink-0" />
                <a
                  data-testid="footer-phone"
                  href={SITE.phoneHref}
                  className="link-underline text-slate-300 hover:text-amber-500"
                >
                  {SITE.phone}
                </a>
              </li>
            </ul>

            <a
              href={SITE.givebutterUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-give-cta"
              className="mt-8 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 font-semibold text-sm transition-colors"
            >
              Give via Givebutter <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            © {new Date().getFullYear()} Midnight Sun Working Dog Coalition · All rights reserved
          </div>
          <div className="flex items-center gap-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Non-clinical framework
            </div>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 hover:text-amber-500 transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
